/* eslint-disable no-console */

import { updateId } from 'expo-updates';
import { Platform } from 'react-native';

import { Loggable } from '@rbi-ctg/frontend';
import { appVersion, binaryRuntimeAppVersionWithBuildNumber } from 'utils/app-version-info';
import { addContext, dataDogLogger } from 'utils/datadog';
import { LogLevel } from 'utils/datadog/types';
import {
  RBIEnv,
  appStartId,
  env,
  getCurrentCommitHash,
  minDevLogLevel,
  region,
} from 'utils/environment';

import {
  ErrorCategory,
  ILoggerMiddleware,
  deviceIdMiddleware,
  errorCategoryMiddleware,
  uuidScrubberMiddleware,
} from './middleware';
import { ILogger, ILoggerBaseFields, ILoggerOptions, LogFunction, Method } from './types';

const methods: Method[] = ['debug', 'error', 'fatal', 'info', 'trace', 'warn'];

const prepareMessage = <O extends Loggable>(
  message: O,
  level: LogLevel,
  loggerAttributes: ILoggerBaseFields
) => {
  if (message instanceof Error) {
    return Object.assign({}, { error: message }, loggerAttributes, { level });
  }

  if (typeof message === 'object') {
    return Object.assign({}, message, loggerAttributes, { level });
  }

  return Object.assign({}, { message }, loggerAttributes, {
    level,
  });
};

/**
 * This function is only exported for testing.
 * The default export for this file is a logger
 * instantiated with fields we want to send
 * on _every_ message to logger.
 * We should only use loggers that are
 * a child of the default logger in production.
 */
export function Logger<T extends object, U extends object>(
  fields: T,
  parentFields?: U,
  loggerOptions: ILoggerOptions = {}
): ILogger {
  const { middlewares = [], currentEnv = env() } = loggerOptions;
  // merge child into parent attributes, then clear undefined attributes
  const loggerAttributes = Object.entries(Object.assign({}, parentFields, fields)).reduce(
    (acc, [key, value]) => (value === undefined ? acc : { ...acc, [key]: value }),
    {}
  );

  const logFunction = (method: Method): LogFunction => message => {
    const level = LogLevel[method];

    type middlewareAttributes = { [key: string]: Loggable };

    // NOTE: If `error` is present uuid-scrubber middleware
    // will replace `message` with `error.message` and
    // append the original `message` property
    // as `originalDeveloperMessage` in attributes
    const attributes = middlewares.reduce<middlewareAttributes>(
      (attr, middleware) => middleware(attr),
      prepareMessage(message, level, loggerAttributes as ILoggerBaseFields) as middlewareAttributes
    );

    const { category } = attributes;

    if (category === ErrorCategory.DoNotLog) {
      return;
    }

    /**
     * Sends events to dataDog
     */
    if (level >= LogLevel.info) {
      dataDogLogger({
        message: attributes.message,
        context: attributes,
        status: LogLevel[method],
      });
    }

    if ((currentEnv === RBIEnv.DEV || __DEV__) && level >= minDevLogLevel()) {
      if (method === 'debug') {
        console.log(message);
        return;
      }
      const consoleMethods = {
        log: console.log,
        info: console.info,
        warn: console.warn,
        error: console.error,
        trace: console.trace,
      };
      const consoleMethod = consoleMethods[method as keyof typeof consoleMethods] || console.error;
      const f = consoleMethod.bind(console);
      f(message);
    }
  };

  const loggerPublicInterface = methods.reduce<ILogger>((logger, method) => {
    return { ...logger, [method]: logFunction(method), [method.toUpperCase()]: LogLevel[method] };
  }, {} as ILogger);

  Object.assign(loggerPublicInterface, {
    group: console.group.bind(console),
    groupCollapsed: console.groupCollapsed.bind(console),
    groupEnd: console.groupEnd.bind(console),
  });

  return loggerPublicInterface;
}

export const loggerBaseFields: ILoggerBaseFields = {
  platform: Platform?.OS as Platform['OS'],
  region,
  stage: env(),
  appStartId,
  userId: undefined, // this is set by AuthStorage to avoid a circular reference that was causing build issues
  buildInfo: {
    updateId,
    commitHash: getCurrentCommitHash(),
    binaryRuntimeVersion: binaryRuntimeAppVersionWithBuildNumber,
    appVersion,
  },
};

let loggingContext = {};

export const addLoggingContext = (context: Object) => {
  loggingContext = { ...loggingContext, ...context };

  if (Platform?.OS === 'web') {
    for (const [key, value] of Object.entries(context)) {
      if (typeof value === 'string' && value.length > 0) {
        addContext(key, value);
      }
    }
    return;
  }

  if ('userId' in context && typeof (context as { userId?: unknown }).userId === 'string') {
    const id = (context as { userId: string }).userId;
    void import('@datadog/mobile-react-native').then(({ DdSdkReactNative }) => {
      DdSdkReactNative.setUserInfo({ id });
    });
  }
};

const loggingContextMiddleware = (attributes => {
  return { ...attributes, ...loggingContext };
}) as ILoggerMiddleware;

const DEFAULT_MIDDLEWARES = [
  errorCategoryMiddleware,
  uuidScrubberMiddleware,
  deviceIdMiddleware,
  loggingContextMiddleware,
];

/**
 * The preferred used export is a configured logger
 * with appVersion, brand, env and platform attributes.
 * Add additional interesting logging context to all log statements with `addLoggingContext`
 */
export const defaultLogger = Logger(loggerBaseFields, {}, { middlewares: DEFAULT_MIDDLEWARES });

export const logger = defaultLogger; // VSCODE auto import works better with this.

export default defaultLogger;
